// Account Status Service
// The DJ's own pause flag (users/{uid}.accountPaused) and the owner's DJ accounts admin (ITER-105).
// The flag and the pause itself are written only by the setDjAccountPaused callable.
// The accepted Terms/Privacy version (users/{uid}.terms, ITER-155) is read from the same doc.

import { doc, onSnapshot, serverTimestamp, setDoc, DocumentData, Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

export type AccountEmailStatus = 'sent' | 'skipped' | 'failed';
export type LiveReason = 'time-window' | 'recent-playback';

export interface AccountStatus {
  accountPaused: boolean;
  termsAcceptedVersion: string | null;
}

export interface DjAccountLiveEvent {
  eventId: string;
  eventName: string;
  reason: LiveReason;
}

export interface DjAccount {
  uid: string;
  email: string;
  displayName: string;
  isOwner: boolean;
  isBeta: boolean;
  disabled: boolean;
  paused: boolean;
  pausedAt: string | null;
  note: string | null;
  liveEvent: DjAccountLiveEvent | null;
  lastSignInAt: string | null;
  linkEditingRevoked: boolean;
}

export interface SetDjAccountPausedResult {
  paused: boolean;
  already?: boolean;
  emailStatus?: AccountEmailStatus;
}

export function docToAccountStatus(data: DocumentData | undefined): AccountStatus {
  const termsVersion = data?.terms?.version;
  return {
    accountPaused: data?.accountPaused === true,
    termsAcceptedVersion: typeof termsVersion === 'string' ? termsVersion : null,
  };
}

export function subscribeToAccountStatus(
  uid: string,
  onChange: (status: AccountStatus) => void,
  onError: (error: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, 'users', uid),
    (snapshot) => onChange(docToAccountStatus(snapshot.data())),
    onError
  );
}

export async function acceptTerms(uid: string, version: string): Promise<void> {
  await setDoc(doc(db, 'users', uid), { terms: { version, acceptedAt: serverTimestamp() } }, { merge: true });
}

function browserTimezoneOffsetMinutes(): number {
  return new Date().getTimezoneOffset();
}

export async function listDjAccounts(): Promise<DjAccount[]> {
  const callable = httpsCallable<{ timezoneOffsetMinutes: number }, { accounts: DjAccount[] }>(functions, 'listDjAccounts');
  const result = await callable({ timezoneOffsetMinutes: browserTimezoneOffsetMinutes() });
  return result.data.accounts;
}

export async function setDjAccountPaused(uid: string, paused: boolean, note?: string): Promise<SetDjAccountPausedResult> {
  const callable = httpsCallable<
    { uid: string; paused: boolean; note: string | null; timezoneOffsetMinutes: number },
    SetDjAccountPausedResult
  >(functions, 'setDjAccountPaused');
  const result = await callable({
    uid,
    paused,
    note: note?.trim() ? note.trim() : null,
    timezoneOffsetMinutes: browserTimezoneOffsetMinutes(),
  });
  return result.data;
}
